import React from 'react';
import {
  TextField,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
} from '@mui/material';

interface ThirdPartyRiskData {
  ofacSanctions: string;
  ofacSanctionsDetails: string;
  angelRelationships: string;
  angelRelationshipsDetails: string;
  personalInfoAccess: string;
  personalInfoDetails: string;
  specialCircumstances: string;
  specialCircumstancesDetails: string;
  paymentTerms?: string;
  projectDeliverables?: string;
  freeFromControl?: string;
  workOutsideOfNormalBusiness?: string;
  independentlyEstablishedBusiness?: string;
  effectiveDate?: string;
  expirationDate?: string;
}

interface ThirdPartyRiskFormProps {
  data: ThirdPartyRiskData;
  onChange: (data: ThirdPartyRiskData) => void;
  disabled?: boolean;
  errors?: { field: string; message: string }[];
  needsIronclad?: boolean;
  showFamilial?: boolean;
}

const ThirdPartyRiskForm: React.FC<ThirdPartyRiskFormProps> = ({ 
  data, 
  onChange, 
  disabled = false, 
  errors = [], 
  needsIronclad = false,
  showFamilial = false
}) => {
  const getFieldError = (fieldName: string) => {
    return errors.find(err => err.field === fieldName)?.message;
  };
  
  const handleChange = (field: keyof ThirdPartyRiskData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({
      ...data,
      [field]: event.target.value,
    });
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Complete the vendor risk assessment below. If you answer 'YES' to any question, this request may be routed to Legal for approval.
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* ACCOUNTING: OFAC, Personal Data, Special Circumstances - ALWAYS SHOWN */}
        <FormControl component="fieldset" sx={{ mt: 2}} disabled={disabled} error={!!getFieldError('ofacSanctions')}>
          <FormLabel component="legend">
            OFAC Sanctions Check
            <br />
            <strong>Before answering, search the vendor name (or its owners/management, if known) using the OFAC Sanctions Search:</strong>
            <br />
            <a href="https://sanctionssearch.ofac.treas.gov/" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
              https://sanctionssearch.ofac.treas.gov/
            </a>
            <br /><br />
            Does the vendor (or its owners/management, if known) appear to match an OFAC sanctions listing?
          </FormLabel>
          <RadioGroup
            value={data.ofacSanctions}
            onChange={handleChange('ofacSanctions')}
            onFocus={(e) => e.preventDefault()}
          >
            <FormControlLabel value="YES" control={<Radio />} label="YES" />
            <FormControlLabel value="NO" control={<Radio />} label="NO" />
          </RadioGroup>
          {getFieldError('ofacSanctions') && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {getFieldError('ofacSanctions')}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="If YES, please add details here"
          value={data.ofacSanctionsDetails}
          onChange={handleChange('ofacSanctionsDetails')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('ofacSanctionsDetails')}
          helperText={getFieldError('ofacSanctionsDetails')}
        />

        <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled} error={!!getFieldError('personalInfoAccess')}>
          <FormLabel component="legend">
            Will the vendor process, transmit, access, or store any customer, client or employee personal data (e.g., names, email addresses, payment information, SSN, health information)?
            <br /><br />
            If so, please contact security@angel.com, describe conversation and include the details of that conversation below:
          </FormLabel>
          <RadioGroup
            value={data.personalInfoAccess}
            onChange={handleChange('personalInfoAccess')}
            onFocus={(e) => e.preventDefault()}
          >
            <FormControlLabel value="YES" control={<Radio />} label="YES" />
            <FormControlLabel value="NO" control={<Radio />} label="NO" />
          </RadioGroup>
          {getFieldError('personalInfoAccess') && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {getFieldError('personalInfoAccess')}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="If YES, please provide details of the personal data and details from the security team conversation."
          value={data.personalInfoDetails}
          onChange={handleChange('personalInfoDetails')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('personalInfoDetails')}
          helperText={getFieldError('personalInfoDetails')}
        />

        <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled} error={!!getFieldError('specialCircumstances')}>
          <FormLabel component="legend">
            Do any of the following circumstances apply to this vendor? If so, please contact Legal to obtain clearance and to add any additional language into the contract.
          </FormLabel>
          <Box sx={{ pl: 2, mb: 2 }}>
            <Typography variant="body2">- Did any government official recommend or require use of this vendor?</Typography>
            <Typography variant="body2">- Is the anticipated contract value above $5,000,000?</Typography>
            <Typography variant="body2">- Will they represent Angel in any way to any government agency, entity, or official?</Typography>
            <Typography variant="body2">- Is the vendor receiving a charitable donation from Angel?</Typography>
            <Typography variant="body2">- Is the vendor a government agency, entity, or official?</Typography>
            <Typography variant="body2">- Is the vendor a media company owned more than 50% by a government entity / official?</Typography>
          </Box>
          <RadioGroup
            value={data.specialCircumstances}
            onChange={handleChange('specialCircumstances')}
            onFocus={(e) => e.preventDefault()}
          >
            <FormControlLabel value="YES" control={<Radio />} label="YES" />
            <FormControlLabel value="NO" control={<Radio />} label="NO" />
          </RadioGroup>
          {getFieldError('specialCircumstances') && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {getFieldError('specialCircumstances')}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="If any of items above are answered Yes, please include the details below"
          value={data.specialCircumstancesDetails}
          onChange={handleChange('specialCircumstancesDetails')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('specialCircumstancesDetails')}
          helperText={getFieldError('specialCircumstancesDetails')}
        />

        {/* BOTH: Familial relationships - only shown when amount >= $1k */}
        {showFamilial && (
          <>
            <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled} error={!!getFieldError('angelRelationships')}>
              <FormLabel component="legend">
                Are you aware of any familial relationships between Angel Studios and this vendor?
                <br />
                <em>(Familial relationships include spouse/partner, parent, child, sibling, or in-laws.)</em>
                <br /><br />
                If so, please contact Legal and describe conversation and details below:
              </FormLabel>
              <RadioGroup
                value={data.angelRelationships}
                onChange={handleChange('angelRelationships')}
                onFocus={(e) => e.preventDefault()}
              >
                <FormControlLabel value="YES" control={<Radio />} label="YES" />
                <FormControlLabel value="NO" control={<Radio />} label="NO" />
              </RadioGroup>
              {getFieldError('angelRelationships') && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {getFieldError('angelRelationships')}
                </Typography>
              )}
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="If YES, please describe the relationships"
              value={data.angelRelationshipsDetails}
              onChange={handleChange('angelRelationshipsDetails')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('angelRelationshipsDetails')}
              helperText={getFieldError('angelRelationshipsDetails')}
            />
          </>
        )}

        {/* IRONCLAD: Payment Terms, Deliverables, 3 Contractor Questions, Contract Dates - when contract needed */}
        {needsIronclad && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 4 }}>
              Contractor/Consultant Agreement Details
            </Typography>

            <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled} error={!!getFieldError('paymentTerms')} fullWidth>
              <FormLabel component="legend">Payment Terms *</FormLabel>
              <RadioGroup
                value={data.paymentTerms || ''}
                onChange={handleChange('paymentTerms')}
                onFocus={(e) => e.preventDefault()}
              >
                <FormControlLabel value="within thirty (30) days after project completion" control={<Radio />} label="Within thirty (30) days after project completion" />
                <FormControlLabel value="within fifteen (15) days after project completion" control={<Radio />} label="Within fifteen (15) days after project completion" />
                <FormControlLabel value="within forty-five (45) days after project completion" control={<Radio />} label="Within forty-five (45) days after project completion" />
                <FormControlLabel value="upon signing the agreement" control={<Radio />} label="Upon signing the agreement" />
                <FormControlLabel value="fifty percent (50%) upon signing the agreement and fifty percent (50%) upon project completion" control={<Radio />} label="Fifty percent (50%) upon signing and fifty percent (50%) upon completion" />
                <FormControlLabel value="Other" control={<Radio />} label="Other" />
              </RadioGroup>
              {getFieldError('paymentTerms') && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {getFieldError('paymentTerms')}
                </Typography>
              )}
            </FormControl>

            <TextField
              required
              fullWidth
              multiline
              rows={4}
              label="Expected Project Deliverables"
              value={data.projectDeliverables || ''}
              onChange={handleChange('projectDeliverables')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('projectDeliverables')}
              helperText={getFieldError('projectDeliverables') || 'Describe the expected deliverables and outcomes for this project'}
            />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 3 }}>
              Independent Contractor Verification
            </Typography>

            <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled} error={!!getFieldError('freeFromControl')}>
              <FormLabel component="legend">
                Will the contractor be independent and free from control or direction from Angel? *
                <br /><br />
                <Typography variant="body2" sx={{ fontWeight: 'normal' }}>
                  If the individual dictates their own process, schedule, and/or the tools used to do the job, they are a contractor.
                  <br /><br />
                  If Angel dictates the process, schedule, and/or tools used, then they are a team member.
                  <br /><br />
                  <em>Example: A graphic designer is hired to create a company logo. The company provides brand guidelines, but doesn't tell the designer which software to use, when to work, or where to work. They would be an independent contractor.</em>
                  <br /><br />
                  <em>On the contrary, if a graphic designer is hired to work for the company full-time, is provided an office, uses the company's software licenses, follows a process outlined by the creative director, and attends all meetings each day to review progress; they are most likely a team member.</em>
                </Typography>
              </FormLabel>
              <RadioGroup
                value={data.freeFromControl || ''}
                onChange={handleChange('freeFromControl')}
                onFocus={(e) => e.preventDefault()}
              >
                <FormControlLabel value="YES" control={<Radio />} label="YES" />
                <FormControlLabel value="NO" control={<Radio />} label="NO" />
              </RadioGroup>
              {getFieldError('freeFromControl') && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {getFieldError('freeFromControl')}
                </Typography>
              )}
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled} error={!!getFieldError('workOutsideOfNormalBusiness')}>
              <FormLabel component="legend">
                Will the contractor be performing work outside of the normal business activities of Angel? *
                <br /><br />
                <Typography variant="body2" sx={{ fontWeight: 'normal' }}>
                  If the individual's work is typically NOT part of Angel's core business activities, they are most likely an independent contractor.
                  <br /><br />
                  If the individual's work is usually integral to Angel's day-to-day operations or core business functions, then they're a team member.
                  <br /><br />
                  <em>Example: A tech startup hires a legal consultant to draft a one-time privacy policy. Legal work is outside the normal company business. Ergo, this would be an independent contractor situation.</em>
                  <br /><br />
                  <em>Example: A software engineer writes code for the app, which is tied to the company's core business. Ergo, they would be a team member.</em>
                </Typography>
              </FormLabel>
              <RadioGroup
                value={data.workOutsideOfNormalBusiness || ''}
                onChange={handleChange('workOutsideOfNormalBusiness')}
                onFocus={(e) => e.preventDefault()}
              >
                <FormControlLabel value="YES" control={<Radio />} label="YES" />
                <FormControlLabel value="NO" control={<Radio />} label="NO" />
              </RadioGroup>
              {getFieldError('workOutsideOfNormalBusiness') && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {getFieldError('workOutsideOfNormalBusiness')}
                </Typography>
              )}
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled} error={!!getFieldError('independentlyEstablishedBusiness')}>
              <FormLabel component="legend">
                Does the contractor have an independently established trade or business? *
                <br /><br />
                <Typography variant="body2" sx={{ fontWeight: 'normal' }}>
                  If the individual runs their own business or trade and offers services to clients outside your company, they are an independent contractor. They typically will have their own branding, marketing structure, and software/tools.
                  <br /><br />
                  If the individual is integrated into Angel's business, working exclusively for you, then they are a team member.
                  <br /><br />
                  <em>Example: A marketing consultant operates "Mad Hatter Marketing." They provide branding, social media strategy, and ad campaigns. They have contracts with various clients. This individual is engaged in an independently established business, therefore they are an independent contractor.</em>
                  <br /><br />
                  <em>Example: A marketing coordinator works on Angel's team full-time, using company-provided software and tools, and following the direction of the marketing director. They don't offer services to external clients. Therefore, they are a team member.</em>
                </Typography>
              </FormLabel>
              <RadioGroup
                value={data.independentlyEstablishedBusiness || ''}
                onChange={handleChange('independentlyEstablishedBusiness')}
                onFocus={(e) => e.preventDefault()}
              >
                <FormControlLabel value="YES" control={<Radio />} label="YES" />
                <FormControlLabel value="NO" control={<Radio />} label="NO" />
              </RadioGroup>
              {getFieldError('independentlyEstablishedBusiness') && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {getFieldError('independentlyEstablishedBusiness')}
                </Typography>
              )}
            </FormControl>

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 3 }}>
              Contract Dates
            </Typography>

            <TextField
              required
              fullWidth
              type="date"
              label="Contract Start Date (Effective Date)"
              value={data.effectiveDate || ''}
              onChange={handleChange('effectiveDate')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('effectiveDate')}
              helperText={getFieldError('effectiveDate')}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              required
              fullWidth
              type="date"
              label="Contract End Date (Expiration Date)"
              value={data.expirationDate || ''}
              onChange={handleChange('expirationDate')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('expirationDate')}
              helperText={getFieldError('expirationDate')}
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default ThirdPartyRiskForm;
